import React, { useCallback } from 'react';

import AnomalyLocation from '../anomaly-location';
import Checklist from '../checklist';
import Conditional from '../../../common/conditional';
import CustomSection from '../../custom-general/custom-section';
import CustomGeneralPhotos from '../../custom-general/custom-general-photos';
import CustomUTMAnomaly from '../../custom-utm/customUTMAnomaly';
import FVIPDOChecklistReport from '../visual-inspection-pdo/fvi-pdo-checklist-report';
import GeneralDrawing from '../general-drawing';
import PID from '../pid';
import PDOSummaryReport from '../pdo-summary';
import PipingAnomalySummaryItem from '../anomaly-summary-item';
import RiskRating from '../../../pages/reports/riskRating';

import { matchPhrases } from '../../../../utils/utilities';
import { FVIPipingItem } from '../../../../utils/models';

import config from '../../../../utils/config';

interface PipingAnalyticsProps {
	items: FVIPipingItem[];
	isChecklistVisible?: boolean;
	useGroupedTitle?: boolean;
	includeEmptyPDONotice?: boolean
	includePDO?: boolean;
}

const PipingAnalytics: React.FC<PipingAnalyticsProps> = ({ items, isChecklistVisible = true, includeEmptyPDONotice = false, includePDO = false, useGroupedTitle = false }) => {
	const getChecklistHeaders = useCallback((item: FVIPipingItem) => {
		return [
			{
				label: 'Line Number',
				value: item.lineNumber
			},
		];
	}, []);

	const getAnomalyHeaders = useCallback((item: FVIPipingItem) => {
		return [
			{
				label: 'Line Number',
				value: item.lineNumber,
			},
		];
	}, []);

	return (
		<>
			<PDOSummaryReport
				includeEmptyReportNotice={includeEmptyPDONotice}
				itemKey='lineNumber' 
				itemLabel='Line Number' 
				items={items} 
			/>

			{items.map((item) => (
				<React.Fragment key={item.lineNumber}>
					{item.pIDImages.map((pid, index: number) => (
						<PID
							hasPID={item.hasPID}
							key={index} 
							pidImages={[pid]} 
							pidNumber={item.pIDNumber} 
							sectionTitle={useGroupedTitle ? "Inspection Findings" : "P & ID"}
							sectionSubtitle={useGroupedTitle ? "P & ID" : undefined} 
							identifier={item.lineNumber}
						/>
					))}

					<GeneralDrawing identifier={item.lineNumber} generalDrawing={item.generalDrawing ?? []} />

					<PipingAnomalySummaryItem
						pipingItem={item} 
						includeSeparator={false} 
						sectionTitle={useGroupedTitle ? "Inspection Findings" : "Anomaly Summary & Description"}
						sectionSubtitle={useGroupedTitle ? "Anomaly Summary & Description" : undefined} 
					/>

					{item.pdoChecklist && (
						<Conditional isVisible={item.pdoChecklist.length > 0}>
							<FVIPDOChecklistReport
								items={item.pdoChecklist}
								label="Line Number"
								value={item.lineNumber}
								hasPDO={matchPhrases(item.hasPDO, 'yes')}
							/>
						</Conditional>
					)}

					<Conditional isVisible={isChecklistVisible}>
						<Checklist
							enableChecklistItemComment={false}
							headers={getChecklistHeaders(item)}
							key={item.lineNumber}
							checklistItems={item.checklistItems}
							additionalComment={item.checklistAdditionalComment}
							sectionTitle={useGroupedTitle ? "Inspection Findings" : "Checklist"}
							sectionSubtitle={useGroupedTitle ? "Checklist" : undefined} 
						/>
					</Conditional>

					<CustomUTMAnomaly
						includePDO={includePDO}
						lineNumberID={item.id}
						headers={getAnomalyHeaders(item)}
						anomalyItems={item.anomalyItems}
					/>

					<Conditional isVisible={matchPhrases(item.hasAnomalies, 'yes')}>
						<AnomalyLocation
							lineNumber={item.lineNumber}
							locationImages={item.anomalyLocations}
						/>
					</Conditional>

					<Conditional isVisible={item.generalPhotos.length > 0}>
						<CustomGeneralPhotos
							identifier={item.lineNumber}
							photos={item.generalPhotos}
							label={useGroupedTitle ? "Inspection Findings" : "Photographic Record"}
							subLabel={useGroupedTitle ? "Photographic Record" : undefined} 
						/>
					</Conditional>
				</React.Fragment>
			))}

			<Conditional isVisible={config.enableRiskRating}>
				<CustomSection sectionTitle="Risk Rating">
					<Conditional isVisible={items.length > 0}>
						<RiskRating data={items} />
					</Conditional>
				</CustomSection>
			</Conditional>
		</>
	);
};

export default PipingAnalytics;
